import React from "react";
import { Input, Select, Table, Tag, Tooltip } from "antd";

import { CATEGORIES, CATEGORY_COLOR, FAMILIES } from "./sampleData";

/**
 * The families table, used on the intake side.
 *
 * One row per procedure FAMILY. The codes underneath are shown comma-separated
 * as labels — a family is the unit a carrier quotes coverage for, and the codes
 * are simply what the practice bills under it.
 *
 * Two columns carry what the old code-per-category model could not:
 *   Category   — our default, editable, because the same procedure sits in
 *                different categories from carrier to carrier (the paper form's
 *                circled "Basic / Major").
 *   Exceptions — hung on the family they qualify rather than pooled in one blob
 *                at the bottom of the page.
 *
 * Frequency is deliberately NOT here: carriers quote it at a finer grain than
 * the family (see FrequencyTable), so it has its own table on both surfaces.
 */
export default function FamiliesTable({ values, onChange, editable }) {
  function setField(key, field, value) {
    if (!onChange) return;
    const next = Object.assign({}, values);
    next[key] = Object.assign({}, next[key] || {}, { [field]: value });
    onChange(next);
  }

  const rows = FAMILIES.map(function (fam) {
    const v = values[fam.key] || {};
    return Object.assign({}, fam, v, {
      key: fam.key,
      codeList: (fam.codes || []).map(function (c) { return c.code; }).join(", "),
      codeNames: (fam.codes || []).map(function (c) { return c.code + " — " + c.name; }).join("\n"),
    });
  });

  const columns = [
    {
      title: "Procedure family",
      dataIndex: "name",
      width: 230,
      render: function (_, row) {
        return (
          <div>
            <div className="bw-fam-name">{row.name}</div>
            <Tooltip title={row.codeNames}>
              <div className="bw-fam-codes">{row.codeList}</div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 170,
      render: function (_, row) {
        const current = row.category || row.defaultCategory;
        const changed = current !== row.defaultCategory;
        if (!editable) {
          return (
            <span>
              <Tag color={CATEGORY_COLOR[current]}>{current}</Tag>
              {changed ? <span className="bw-changed">changed</span> : null}
            </span>
          );
        }
        return (
          <div>
            <Select
              size="small" style={{ width: 132 }} value={current}
              onChange={function (val) { setField(row.key, "category", val); }}
              options={CATEGORIES.map(function (c) { return { value: c, label: c }; })}
            />
            {changed ? (
              <Tooltip title={"Default for this carrier is " + row.defaultCategory}>
                <span className="bw-changed">changed</span>
              </Tooltip>
            ) : null}
            {row.askBasicMajor && !changed ? (
              <div className="bw-fam-codes" style={{ marginTop: 4 }}>ask: Basic or Major?</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Coverage",
      dataIndex: "pct",
      width: 100,
      render: function (_, row) {
        if (!editable) return row.pct ? row.pct + "%" : <span className="bw-notdue">—</span>;
        return (
          <Input size="small" suffix="%" style={{ width: 80 }} value={row.pct || ""}
            onChange={function (e) { setField(row.key, "pct", e.target.value); }} />
        );
      },
    },
    {
      title: "Exceptions",
      dataIndex: "exception",
      width: editable ? 250 : 280,
      render: function (_, row) {
        if (!editable) {
          return row.exception
            ? <span className="bw-row-exception">{row.exception}</span>
            : <span className="bw-notdue">—</span>;
        }
        return (
          <Input size="small" placeholder="anything that departs from the usual rule"
            value={row.exception || ""}
            onChange={function (e) { setField(row.key, "exception", e.target.value); }} />
        );
      },
    },
  ];

  if (editable) {
    // Orthodontics is the one family carrying an annual AND a lifetime maximum,
    // so its extra fields ride under the row rather than earning columns nobody
    // else would fill.
    columns.push({
      title: "",
      width: 240,
      render: function (_, row) {
        if (!row.isOrtho) return null;
        return (
          <div className="bw-ortho-extra">
            <Input size="small" placeholder="Lifetime max $" value={row.orthoLTM || ""}
              onChange={function (e) { setField(row.key, "orthoLTM", e.target.value); }} />
            <Input size="small" placeholder="Age limit" value={row.orthoAgeLimit || ""}
              onChange={function (e) { setField(row.key, "orthoAgeLimit", e.target.value); }} />
          </div>
        );
      },
    });
  }

  return (
    <Table size="small" rowKey="key" columns={columns} dataSource={rows}
      pagination={false} tableLayout="fixed" />
  );
}
